import { observer } from 'mobx-react-lite';
import { default as FeedbackContainer, FeedbackType } from './FeedbackContainer';
import FeedbackContainerOld from './FeedbackContainerOld';
import RelatedContentSender from './RelatedContentSender';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { Icon } from '@fluentui/react/lib/Icon';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getIconForFile, CloudIconWithOverlay } from 'owa-file-icon';
import loc, { format } from 'owa-localize';
import type {
    FileInsightType,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import { getInsightsTraceId, lazyOpenFileInsight } from 'owa-meeting-insights';
import {
    sentAtLabelSentenceCase,
    modifiedAtLabelSentenceCase,
} from 'owa-meeting-insights/lib/components/InsightDocRow.locstring.json';
import { getFileInsightAriaLabel } from 'owa-meeting-insights/lib/helpers/ariaLabelHelpers';
import { getIsOrganizerFromMeetingId } from 'owa-meeting-insights/lib/selectors/instrumentationSelectors';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import * as React from 'react';
import { logFeedbackButtonClicked } from '../helpers/instrumentationHelpers';

import styles from './RelatedFile.scss';

export interface RelatedFileProps {
    colorScheme: ColorScheme;
    file: FileInsightType;
    meetingId: string;
    readingPaneEntrySource?: ReadingPaneEntrySourceType; // null when rendering from CITI card
    insightsEntrySource: InsightsDataFetchSource;
    position: number;
    renderedFilesCount: number;
}

export default observer(function RelatedFile(props: RelatedFileProps) {
    const renderFirstRow = (): JSX.Element => {
        // Sender can be null when the file source is from SharePoint
        const { senderName, senderEmailAddress, timestamp, lastActivityType } = props.file;
        const actionText =
            lastActivityType === 'send'
                ? loc(sentAtLabelSentenceCase)
                : loc(modifiedAtLabelSentenceCase);
        const timestampToDisplay = format(actionText, timestamp);
        return (
            <div className={styles.firstRow}>
                <div className={styles.firstColumn}>
                    <RelatedContentSender
                        name={senderName}
                        emailAddress={senderEmailAddress}
                        size={PersonaSize.size24}
                        personaType={'NotResolved'}
                    />
                </div>
                <div className={styles.secondColumn}>
                    <span className={styles.senderName} title={senderName || senderEmailAddress}>
                        {senderName || senderEmailAddress}
                    </span>
                    <span className={styles.timestamp} title={timestampToDisplay}>
                        {timestampToDisplay}
                    </span>
                </div>
            </div>
        );
    };
    const renderSecondRow = (): JSX.Element => {
        const { sharingSubject, source, fileFullName } = props.file;
        const secondLineText = sharingSubject || source;
        // Format the tooltip text and add a line breaker in between file name and second line text
        const tooltipText = fileFullName + '\n' + secondLineText;
        return (
            <div className={styles.secondRow}>
                <div className={styles.fileIcon}>{renderFileIcon()}</div>
                <div className={styles.fileDetails} title={tooltipText}>
                    <span className={styles.fileName}>{fileFullName}</span>
                    <span className={styles.sourceOrEmailSubject}>{secondLineText}</span>
                </div>
            </div>
        );
    };
    const renderReasonMarkerRow = (): JSX.Element => {
        const { reasonMarker, senderName } = props.file;
        const reasonMarkerText = reasonMarker ? reasonMarker : 'Shared by ' + senderName + '.';
        // VSO 109482: Update code to handle new API with parameters and reasonCodes to generate localized strings;
        return (
            <div className={styles.reasonMarkerRow}>
                <span className={styles.reasonMarker}>{reasonMarkerText}</span>
            </div>
        );
    };
    const renderFileIcon = (): JSX.Element => {
        const { isCloudyFile, fileFullName } = props.file;
        return isCloudyFile ? (
            <CloudIconWithOverlay
                fileName={fileFullName}
                contentType={null}
                isBlocked={false}
                isFolder={false}
                embeddedItemClass={null}
                fileIconStyle={styles.cloudFileIcon}
            />
        ) : (
            <Icon className={getIconForFile(fileFullName).medium} />
        );
    };

    const relatedFileContainerRef = React.useRef<HTMLDivElement>();

    const onUsefulButtonClicked = () => {
        onFileFeedbackButtonClicked(true /* isUseful */);
    };
    const onNotUsefulButtonClicked = () => {
        onFileFeedbackButtonClicked(false /* isUseful */);
    };
    const onFileFeedbackButtonClicked = (isUseful: boolean) => {
        relatedFileContainerRef.current?.focus();

        const { file, meetingId, insightsEntrySource } = props;
        const traceId = getInsightsTraceId(meetingId, insightsEntrySource);
        const isOrganizer = getIsOrganizerFromMeetingId(meetingId);
        // We are re-purposing itemId to check if a file is explicit file, which means they're explicitly attached to a calendar event
        // so that they'll have an itemId
        logFeedbackButtonClicked(
            meetingId,
            isUseful,
            traceId,
            file.referenceId,
            isOrganizer,
            file.relevanceRelationship,
            insightsEntrySource,
            FeedbackType.File
        );
    };

    const onRelatedFileClicked = (evt: React.MouseEvent<HTMLElement>) => {
        const { meetingId, file, readingPaneEntrySource, insightsEntrySource } = props;
        evt.stopPropagation();
        lazyOpenFileInsight.importAndExecute(
            meetingId,
            file,
            readingPaneEntrySource,
            insightsEntrySource
        );
    };
    const onKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        const { meetingId, file, readingPaneEntrySource, insightsEntrySource } = props;
        if (evt.which === KeyCodes.enter) {
            lazyOpenFileInsight.importAndExecute(
                meetingId,
                file,
                readingPaneEntrySource,
                insightsEntrySource
            );
        }
    };

    const isOrganizer = getIsOrganizerFromMeetingId(props.meetingId);
    const traceId = getInsightsTraceId(props.meetingId, props.insightsEntrySource);
    if (!traceId) {
        trace.warn('RelatedFile: invalid traceId');
    }

    return (
        <div
            ref={relatedFileContainerRef}
            className={styles.relatedFileContainer}
            role="listitem"
            tabIndex={0}
            data-is-focusable="true" // needed for FocusZone management to work correctly
            aria-label={getFileInsightAriaLabel(props.file)}
            aria-posinset={props.position + 1}
            aria-setsize={props.renderedFilesCount}>
            <div
                className={styles.relatedFile}
                onClick={onRelatedFileClicked}
                onKeyDown={onKeyDown}>
                {isFeatureEnabled('cal-meeting-insights-reasonMarkers')
                    ? renderReasonMarkerRow()
                    : renderFirstRow()}
                {renderSecondRow()}
                <div className={styles.feedbackHoverTarget} onClick={onFeedbackContainerClicked}>
                    {isFeatureEnabled('cal-meeting-insights-feedbackUI') ? (
                        <FeedbackContainer
                            colorScheme={props.colorScheme}
                            traceId={traceId}
                            feedbackType={FeedbackType.File}
                            isOrganizer={isOrganizer}
                            relevanceRelationship={props.file.relevanceRelationship}
                            referenceId={props.file.referenceId}
                            entrySource={props.insightsEntrySource}
                            meetingId={props.meetingId}
                            itemId={props.file.id}
                        />
                    ) : (
                        <FeedbackContainerOld
                            colorScheme={props.colorScheme}
                            onUsefulButtonClicked={onUsefulButtonClicked}
                            onNotUsefulButtonClicked={onNotUsefulButtonClicked}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

function onFeedbackContainerClicked(evt: React.MouseEvent<any>) {
    // Suppress the onClick callback of feedback UX, otherwise it'll open the file.
    // This is needed because feedbackHoverTarget needs to be part of relatedFile, so that it shows up upon hovering over the file content,
    evt.stopPropagation();
}
